/* Chronoera: progressive scroll entrances. Nothing is hidden while waiting for JS. */
(() => {
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const registered = new WeakSet();
  const pending = new Set();
  const running = new Map();
  const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

  function finish(element) {
    running.get(element)?.cancel();
    running.delete(element);
    pending.delete(element);
    observer.unobserve(element);
    element.dataset.chMotionState = 'done';
  }

  function reveal(element) {
    observer.unobserve(element);
    pending.delete(element);
    element.dataset.chMotionState = 'done';
    if (reducedMotion.matches || document.hidden || element.matches(':focus-within')) return;

    const effect = element.dataset.chMotion;
    const compact = mobile.matches;
    const distance = compact ? 14 : 28;
    let from = { opacity: 0.08, transform: `translate3d(0, ${distance}px, 0)` };
    let duration = compact ? 580 : 820;

    if (effect === 'left' || effect === 'right') {
      if (!compact) from.transform = `translate3d(${effect === 'left' ? -28 : 28}px, 0, 0)`;
      duration = compact ? 580 : 900;
    } else if (effect === 'photo') {
      from = { opacity: 0.5, transform: `scale(${compact ? 1.018 : 1.035})` };
      duration = compact ? 800 : 1150;
    } else if (effect === 'fade') {
      from = { opacity: 0.12, transform: 'none' };
      duration = compact ? 500 : 780;
    }

    const delay = Math.min(Number(element.dataset.chMotionDelay) || 0, compact ? 90 : 180);
    try {
      const animation = element.animate(
        [from, { opacity: 1, transform: 'none' }],
        { duration, delay, easing, fill: 'backwards' }
      );
      running.set(element, animation);
      const clear = () => {
        if (running.get(element) === animation) running.delete(element);
      };
      animation.addEventListener('finish', clear, { once: true });
      animation.addEventListener('cancel', clear, { once: true });
    } catch {
      // Unsupported animation options must never affect readability.
      finish(element);
    }
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) reveal(entry.target);
    });
  }, { threshold: 0, rootMargin: '0px 0px -24px 0px' });

  function register(element, effect = 'rise', delay = 0) {
    if (!element || registered.has(element)) return;
    registered.add(element);
    element.dataset.chMotion = effect;
    element.dataset.chMotionDelay = String(delay);
    const rect = element.getBoundingClientRect();
    // The initial viewport, purchase controls and restored scroll positions stay immediate.
    if (reducedMotion.matches || (rect.height > 0 && rect.top < window.innerHeight - 24)) {
      element.dataset.chMotionState = 'done';
      return;
    }
    element.dataset.chMotionState = 'waiting';
    pending.add(element);
    observer.observe(element);
  }

  function items(root, selector, effect = 'rise', stagger = 0) {
    root.querySelectorAll(selector).forEach((element, index) => {
      register(element, effect, Math.min(index, 3) * stagger);
    });
  }

  function photo(frame) {
    if (!frame) return;
    const image = frame.querySelector('img');
    if (!image) return;
    frame.dataset.chMotionFrame = '';
    register(image, 'photo');
  }

  function home(root) {
    root.querySelectorAll('.ch-heading').forEach((heading) => items(heading, ':scope > *', 'rise', 90));
    root.querySelectorAll('.ch-benefits, .ch-products, .ch-brands__row, .ch-platforms__row, .ch-service__grid')
      .forEach((group) => items(group, ':scope > *', 'rise', 65));
    root.querySelectorAll('.ch-world').forEach((world) => {
      photo(world);
      items(world, ':scope > div', 'rise');
    });
    photo(root.querySelector('.ch-focus__image'));
    items(root, '.ch-focus__copy', 'right');
    items(root, '.ch-story > :first-child', 'left');
    items(root, '.ch-story__copy', 'right');
    items(root, '.ch-review-summary', 'rise');
    items(root, '.ch-review-layout > chronoera-reviews, .ch-customer-reviews > chronoera-reviews', 'fade');
    root.querySelectorAll('.ch-guides__grid').forEach((group) => items(group, ':scope > *', 'rise', 90));
  }

  function seastar(root) {
    root.querySelectorAll('.sea-editorial__heading, .ch-sea-section-head').forEach((heading) => {
      items(heading, ':scope > *', 'rise', 70);
    });
    root.querySelectorAll('.sea-chapter').forEach((chapter) => {
      photo(chapter.querySelector('.sea-chapter__image'));
      const reverse = chapter.classList.contains('sea-chapter--reverse');
      items(chapter, '.sea-chapter__copy', reverse ? 'left' : 'right');
    });
    photo(root.querySelector('.sea-quartz__image'));
    items(root, '.sea-quartz__panel', 'rise');
    items(root, '.sea-care > :first-child', 'left');
    root.querySelectorAll('.sea-care__items, .ch-sea-colours__grid').forEach((group) => {
      items(group, ':scope > *', 'rise', 70);
    });
  }

  function about(root) {
    root.querySelectorAll('.media-with-content').forEach((chapter) => {
      photo(chapter.querySelector('.media-block'));
      const rightImage = chapter.classList.contains('media-with-content--media-right');
      const content = chapter.querySelector('.media-with-content__content');
      if (content) {
        items(content, '.text-block', rightImage ? 'left' : 'right', 70);
        items(content, 'a.button, a.link', 'rise');
      }
    });
    root.querySelectorAll('.section-content-wrapper .image-block').forEach(photo);
    root.querySelectorAll('.section-content-wrapper').forEach((section) => {
      items(section, '.text-block', 'rise', 70);
      items(section, 'a.button, a.link', 'fade');
    });
  }

  function initialize() {
    document.querySelectorAll('[data-chronoera-motion]').forEach((root) => {
      const page = root.dataset.chronoeraMotion;
      if (page === 'home') home(root);
      if (page === 'seastar') seastar(root);
      if (page === 'about') about(root);
    });
  }

  // Interaction always wins over decoration, including newly selected feature panels.
  const endInteracting = (event) => {
    for (const element of pending) {
      if (element.contains(event.target)) finish(element);
    }
    for (const element of running.keys()) {
      if (element.contains(event.target)) finish(element);
    }
  };
  document.addEventListener('focusin', endInteracting);
  document.addEventListener('pointerdown', endInteracting, { passive: true });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const element of [...pending, ...running.keys()]) {
      if (event.target.contains(element)) finish(element);
    }
  });
  const finishAll = () => {
    for (const element of [...pending, ...running.keys()]) finish(element);
  };
  reducedMotion.addEventListener('change', (event) => { if (event.matches) finishAll(); });
  window.addEventListener('beforeprint', finishAll);
  window.addEventListener('pagehide', finishAll);
  initialize();
})();
